// frontend/src/pages/AccountPage.js
import React, { useState, useEffect } from 'react';
import useAuth from '../hooks/useAuth';
import { updateUserDetails, changePassword } from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { UserCircleIcon, KeyIcon, IdentificationIcon } from '@heroicons/react/24/outline';

const AccountPage = () => {
  const { currentUser, isLoading: authLoading, fetchUserDetails } = useAuth(); // fetchUserDetails to refresh after update

  // State for user details form
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsMessage, setDetailsMessage] = useState('');
  const [detailsError, setDetailsError] = useState('');

  // State for password change form
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword1, setNewPassword1] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    if (currentUser) {
      setFirstName(currentUser.first_name || '');
      setLastName(currentUser.last_name || '');
    }
  }, [currentUser]);

  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    setDetailsLoading(true);
    setDetailsMessage('');
    setDetailsError('');
    try {
      await updateUserDetails({ first_name: firstName, last_name: lastName });
      setDetailsMessage('Your details have been updated successfully!');
      // Refresh currentUser in AuthContext after successful update
      if (fetchUserDetails && currentUser?.token) { // Assuming token is stored in currentUser or accessible
        await fetchUserDetails(localStorage.getItem('authToken')); // Or pass token directly
      } else if (fetchUserDetails) {
        await fetchUserDetails(localStorage.getItem('authToken'));
      }
    } catch (error) {
      setDetailsError(error.detail || error.message || 'Failed to update details.');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (newPassword1 !== newPassword2) {
      setPasswordError("New passwords do not match.");
      return;
    }
    setPasswordLoading(true);
    setPasswordMessage('');
    setPasswordError('');
    try {
      await changePassword({ old_password: oldPassword, new_password1: newPassword1, new_password2: newPassword2 });
      setPasswordMessage('Password changed successfully!');
      setOldPassword('');
      setNewPassword1('');
      setNewPassword2('');
    } catch (error) {
        // dj-rest-auth returns errors in a specific format
        if (error && typeof error === 'object') {
            let messages = [];
            for (const key in error) {
                if (Array.isArray(error[key])) {
                    messages.push(`${key.replace("_", " ")}: ${error[key].join(", ")}`);
                } else {
                    messages.push(error[key]);
                }
            }
            setPasswordError(messages.join("; ") || 'Failed to change password.');
        } else {
            setPasswordError(error.message || 'Failed to change password.');
        }
    } finally {
      setPasswordLoading(false);
    }
  };

  if (authLoading || !currentUser) {
    return (
      <div className="flex justify-center items-center flex-grow p-10">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8 max-w-3xl">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-8">Account Settings</h1>

      {/* User Information Section */}
      <div className="bg-white shadow-lg rounded-lg p-6 sm:p-8 mb-8 border border-gray-200">
        <div className="flex items-center mb-6">
          <UserCircleIcon className="h-8 w-8 text-blue-600 mr-3" />
          <h2 className="text-xl font-semibold text-gray-700">Your Information</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-500">Email</label>
            <p className="mt-1 text-md text-gray-800 bg-gray-100 px-3 py-2 rounded-md">{currentUser.email}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Username</label>
            <p className="mt-1 text-md text-gray-800 bg-gray-100 px-3 py-2 rounded-md">{currentUser.username || 'Not set'}</p>
          </div>

          <form onSubmit={handleDetailsSubmit} className="space-y-4 pt-4 border-t border-gray-100">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Your first name"
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Your last name"
              />
            </div>
            {detailsMessage && <p className="text-sm text-green-600 bg-green-50 p-2 rounded-md">{detailsMessage}</p>}
            {detailsError && <p className="text-sm text-red-600 bg-red-50 p-2 rounded-md">{detailsError}</p>}
            <button
              type="submit"
              disabled={detailsLoading}
              className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-2.5 border border-transparent text-sm font-semibold rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60 transition-colors"
            >
              {detailsLoading ? <LoadingSpinner /> : 'Save Changes'}
            </button>
          </form>
        </div>
      </div>

      {/* Change Password Section */}
      <div className="bg-white shadow-lg rounded-lg p-6 sm:p-8 border border-gray-200">
        <div className="flex items-center mb-6">
          <KeyIcon className="h-8 w-8 text-blue-600 mr-3" />
          <h2 className="text-xl font-semibold text-gray-700">Change Password</h2>
        </div>
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label htmlFor="oldPassword" className="block text-sm font-medium text-gray-700">Current Password</label>
            <input
              type="password"
              id="oldPassword"
              name="oldPassword"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="newPassword1" className="block text-sm font-medium text-gray-700">New Password</label>
            <input
              type="password"
              id="newPassword1"
              name="newPassword1"
              value={newPassword1}
              onChange={(e) => setNewPassword1(e.target.value)}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="newPassword2" className="block text-sm font-medium text-gray-700">Confirm New Password</label>
            <input
              type="password"
              id="newPassword2"
              name="newPassword2"
              value={newPassword2}
              onChange={(e) => setNewPassword2(e.target.value)}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
          {passwordMessage && <p className="text-sm text-green-600 bg-green-50 p-2 rounded-md">{passwordMessage}</p>}
          {passwordError && <p className="text-sm text-red-600 bg-red-50 p-2 rounded-md">{passwordError}</p>}
          <button
            type="submit"
            disabled={passwordLoading}
            className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-2.5 border border-transparent text-sm font-semibold rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60 transition-colors"
          >
            {passwordLoading ? <LoadingSpinner /> : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AccountPage;
